import { createTheme, alpha } from "@mui/material";

/**
 * THE KINETIC ATELIER - ADMINISTRATIVE COMMAND CORE
 * Design North Star: "The Digital Architect"
 * Implementation: Industry-standard professional creative suite.
 */
export const getAdminTheme = (mode = "light") => {
  const isDark = mode === "dark";

  // Design Tokens from Kinetic Atelier System
  const tokens = {
    primary: isDark ? "#c0c1ff" : "#6366f1",
    primaryContainer: isDark ? "#8083ff" : "#8083ff",
    surface: isDark ? "#131313" : "#f4f5fa",
    surfaceLow: isDark ? "#1c1b1b" : "#ffffff",
    surfaceLowest: isDark ? "#0e0e0e" : "#eef0f7",
    surfaceHigh: isDark ? "#2a2a2a" : "#ffffff",
    surfaceHighest: isDark ? "#353534" : "#ffffff",
    textPrimary: isDark ? "#e5e2e1" : "#131316",
    textSecondary: isDark ? "#c7c4d7" : "#64748b",
  };

  return createTheme({
    palette: {
      mode,
      primary: {
        main: tokens.primary,
        light: alpha(tokens.primary, 0.8),
        dark: alpha(tokens.primary, 0.9),
        contrastText: isDark ? "#1000a9" : "#ffffff",
      },
      background: {
        default: tokens.surface,
        paper: tokens.surfaceLow,
      },
      text: {
        primary: tokens.textPrimary,
        secondary: tokens.textSecondary,
      },
      divider: alpha(tokens.textPrimary, 0.08),
      success: { main: "#10b981" },
      warning: { main: "#ffb783" },
      error: { main: "#ef4444" },
    },
    typography: {
      fontFamily: '"Inter", "Plus Jakarta Sans", sans-serif',
      h1: { fontSize: "4.5rem", fontWeight: 950, letterSpacing: "-0.05em", color: tokens.textPrimary },
      h2: { fontSize: "3.25rem", fontWeight: 950, letterSpacing: "-0.04em", color: tokens.textPrimary },
      h3: { fontSize: "2.5rem", fontWeight: 900, letterSpacing: "-0.03em", color: tokens.textPrimary },
      h4: { fontSize: "1.85rem", fontWeight: 900, letterSpacing: "-0.02em", color: tokens.textPrimary },
      h5: { fontSize: "1.45rem", fontWeight: 850, letterSpacing: "-0.02em", color: tokens.textPrimary },
      h6: { fontSize: "1.1rem", fontWeight: 800, color: tokens.textPrimary },
      body1: { fontSize: "1rem", lineHeight: 1.6, fontWeight: 700, color: tokens.textPrimary },
      body2: { fontSize: "0.875rem", lineHeight: 1.6, fontWeight: 700, color: tokens.textSecondary },
      button: { textTransform: "none", fontWeight: 900, letterSpacing: "-0.01em" },
      caption: { fontWeight: 800, letterSpacing: "0.05em", textTransform: 'uppercase', fontSize: '0.65rem' },
    },
    shape: {
      borderRadius: 48, // Atomic curvature
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          body {
            background-color: ${tokens.surface};
            color: ${tokens.textPrimary};
            transition: background-color 0.5s cubic-bezier(0.4, 0, 0.2, 1);
            -webkit-font-smoothing: antialiased;
          }
          /* Custom Studio Scrollbar */
          ::-webkit-scrollbar { width: 6px; }
          ::-webkit-scrollbar-track { background: transparent; }
          ::-webkit-scrollbar-thumb { 
            background: ${alpha(tokens.primary, 0.2)};
            border-radius: 10px;
          }
          ::-webkit-scrollbar-thumb:hover { background: ${alpha(tokens.primary, 0.4)}; }
        `,
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: tokens.surfaceLow,
            border: `1px solid ${alpha(tokens.textPrimary, 0.04)}`,
            boxShadow: isDark 
              ? "0 30px 60px rgba(0, 0, 0, 0.5)" 
              : "0 20px 40px rgba(0, 0, 0, 0.05)",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: "16px",
            padding: "12px 32px",
            "&:hover": {
              transform: "translateY(-2px)",
              filter: "brightness(1.1)",
            },
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${tokens.primaryContainer}, ${tokens.primary})`,
            boxShadow: `0 8px 16px ${alpha(tokens.primary, 0.2)}`,
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: "filled" },
        styleOverrides: {
          root: {
            "& .MuiFilledInput-root": {
              backgroundColor: tokens.surfaceLowest,
              borderRadius: "18px",
              border: `1px solid ${alpha(tokens.textPrimary, 0.05)}`,
              "&:hover": { backgroundColor: tokens.surfaceLowest },
              "&.Mui-focused": {
                borderColor: tokens.primary,
                boxShadow: `0 0 0 4px ${alpha(tokens.primary, 0.1)}`,
              },
            },
          },
        },
      },
      MuiFilledInput: {
        defaultProps: { disableUnderline: true },
      },
      MuiInput: {
        defaultProps: { disableUnderline: true },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: "56px",
            padding: "16px",
            backgroundColor: tokens.surfaceHigh,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${alpha(tokens.textPrimary, 0.04)}` },
          head: { fontWeight: 900, color: tokens.textSecondary, fontSize: '0.7rem' }
        }
      }
    },
  });
};

export default getAdminTheme;
